import { apiService } from './apiService';
import { DEFAULT_ROLES, ModulePermission, PermissionAction, Role } from '../types/roles';

type BackendRole = {
  id: string;
  name: string;
  description: string;
  is_system_role?: number | boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  permission_count?: number;
  permissions?: BackendRolePermission[];
};

type BackendRolePermission = {
  module_name: string;
  can_create?: number | boolean;
  can_read?: number | boolean;
  can_update?: number | boolean;
  can_delete?: number | boolean;
  can_export?: number | boolean;
  can_approve?: number | boolean;
  can_reject?: number | boolean;
};

const permissionActions: Array<{ action: PermissionAction; field: keyof BackendRolePermission }> = [
  { action: 'create', field: 'can_create' },
  { action: 'read', field: 'can_read' },
  { action: 'update', field: 'can_update' },
  { action: 'delete', field: 'can_delete' },
  { action: 'export', field: 'can_export' },
  { action: 'approve', field: 'can_approve' },
  { action: 'reject', field: 'can_reject' },
];

const mapBackendPermission = (permission: BackendRolePermission): ModulePermission => ({
  module: permission.module_name as ModulePermission['module'],
  actions: permissionActions
    .filter(({ field }) => Boolean(permission[field]))
    .map(({ action }) => action),
});

const mapBackendRole = (role: BackendRole): Role => ({
  id: role.id,
  name: role.name,
  description: role.description || '',
  isSystemRole: Boolean(role.is_system_role),
  permissions: (role.permissions || []).map(mapBackendPermission),
  createdAt: role.created_at || new Date().toISOString(),
  updatedAt: role.updated_at || role.created_at || new Date().toISOString(),
  createdBy: role.created_by,
});

const toBackendPermissions = (permissions: ModulePermission[]) =>
  permissions.map((permission) => ({
    module_name: permission.module,
    can_create: permission.actions.includes('create') ? 1 : 0,
    can_read: permission.actions.includes('read') ? 1 : 0,
    can_update: permission.actions.includes('update') ? 1 : 0,
    can_delete: permission.actions.includes('delete') ? 1 : 0,
    can_export: permission.actions.includes('export') ? 1 : 0,
    can_approve: permission.actions.includes('approve') ? 1 : 0,
    can_reject: permission.actions.includes('reject') ? 1 : 0,
  }));

const syncRolesCache = (roles: Role[]) => {
  localStorage.setItem('roles', JSON.stringify(roles));
};

const defaultRoleMap = new Map(DEFAULT_ROLES.map((role) => [role.id, role]));

const withFallbackPermissions = (role: Role): Role => {
  if (role.permissions.length > 0) return role;
  const fallback = defaultRoleMap.get(role.id);
  return fallback ? { ...role, permissions: [...fallback.permissions] } : role;
};

export const roleService = {
  async getRoles(): Promise<Role[]> {
    const response = await apiService.get<BackendRole[]>('/roles');
    const roles = (response.data || []).map((role) => withFallbackPermissions(mapBackendRole(role)));

    syncRolesCache(roles);
    return roles;
  },

  async getAssignedRoles(): Promise<Role[]> {
    const response = await apiService.get<BackendRole[]>('/roles/assigned');
    const roles = (response.data || []).map((role) => withFallbackPermissions(mapBackendRole(role)));
    return roles;
  },

  async getRole(id: string): Promise<Role> {
    const response = await apiService.get<BackendRole>(`/roles/${encodeURIComponent(id)}`);
    return withFallbackPermissions(mapBackendRole(response.data as BackendRole));
  },

  async createRole(role: Pick<Role, 'id' | 'name' | 'description' | 'permissions'>, createdBy?: string | number) {
    await apiService.post('/roles', {
      id: role.id,
      name: role.name,
      description: role.description,
      createdBy,
    });

    await apiService.put(`/roles/${encodeURIComponent(role.id)}/permissions`, {
      permissions: toBackendPermissions(role.permissions),
    });

    return this.getRole(role.id);
  },

  async updateRole(roleId: string, role: Pick<Role, 'name' | 'description' | 'permissions'>, options?: { skipRoleUpdate?: boolean }) {
    if (!options?.skipRoleUpdate) {
      await apiService.put(`/roles/${encodeURIComponent(roleId)}`, {
        name: role.name,
        description: role.description,
      });
    }

    await apiService.put(`/roles/${encodeURIComponent(roleId)}/permissions`, {
      permissions: toBackendPermissions(role.permissions),
    });

    return this.getRole(roleId);
  },

  async deleteRole(roleId: string) {
    return await apiService.delete(`/roles/${encodeURIComponent(roleId)}`);
  },

  syncRolesCache,
};
